import { useState, useEffect } from 'react';
import { Loader2, User, X, Search } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getGroupMembers, isGroupEditor, removeGroupMember } from '@/lib/firebase/queries/group-members';
import { addMemberOrOfferMove } from '@/lib/groups/addMemberOrOfferMove';
import { isOrganizationManager } from '@/lib/firebase/queries/organizations/isOrganizationMember';
import { searchUsersForGroup, type GroupSearchResult } from '@/lib/firebase/queries/groupMemberSearch';
import { getOrganizationMembers } from '@/lib/firebase/queries/organizations/getOrganizationMembers';
import {
  candidateUid,
  groupAddMode,
  orgCandidates,
  personalSearchPhone,
  searchOrgCandidates,
  type CandidatePerson,
} from '@/lib/groups/groupCandidates';
import { maskPhone } from '@/lib/privacy/maskPhone';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { Group } from '@/types/support';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import type { VirtualMember } from '@/lib/firebase/queries/virtual-membership';
import { memberDisplayName } from '@/lib/groups/memberName';

interface GroupMembersProps {
  group: Group;
}

// getGroupMembersVirtual rows: name fields + email, no firstName/lastName
// guaranteed (rendering those showed "undefined undefined").
type Member = VirtualMember;

export function GroupMembers({ group }: GroupMembersProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<GroupSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const { t, language } = useLanguage();
  const { user } = useAuth();
  // Owner decision #3: an org group adds only its organization's
  // beneficiaries; a personal group adds by exact phone number (server-side
  // searchUsersForGroup). Nothing here reads the whole `users` collection.
  const addMode = groupAddMode(group);
  const [orgPeople, setOrgPeople] = useState<CandidatePerson[] | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        // Use virtual membership system for both groups and sections
        const { getGroupMembersVirtual } = await import('@/lib/firebase/queries/virtual-membership');
        const [allMembersData, editorStatus] = await Promise.all([
          getGroupMembersVirtual(group.id),
          isGroupEditor(group.id, user.uid)
        ]);
        
        console.log(`🔍 Checking permissions for user ${user.uid} in group ${group.id}:`);
        console.log(`  - Is editor: ${editorStatus}`);
        console.log(`  - Is creator: ${group.creatorId === user.uid}`);
        console.log(`  - Organization: ${group.organization || 'none'}`);
        
        // Check if user is an organization manager (if group belongs to an organization)
        let canManage = editorStatus;
        
        // Also check if user is the creator
        if (!canManage && group.creatorId === user.uid) {
          console.log(`✅ User is creator, granting edit permissions`);
          canManage = true;
        }
        
        if (!canManage && group.organization) {
          const isOrgManager = await isOrganizationManager(group.organization, user.uid);
          console.log(`  - Is org manager: ${isOrgManager}`);
          canManage = isOrgManager;
        }
        
        console.log(`  - Final canManage: ${canManage}`);
        
        // Organization managers are listed like any member, as on mobile
        // (owner decision: no web-only hiding).
        const membersData = allMembersData;

        // Sort members to show editors first
        const sortedMembers = [...membersData].sort((a, b) => {
          if (a.role === 'editor' && b.role !== 'editor') return -1;
          if (a.role !== 'editor' && b.role === 'editor') return 1;
          return 0;
        });
        setMembers(sortedMembers); 
        setCanManageMembers(canManage);
      } catch (error) {
        console.error('Error fetching group members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [group.id, user]);

  // Org group: the manager screens' beneficiary list, loaded once.
  useEffect(() => {
    if (!canManageMembers || addMode !== 'org' || !group.organization) return;
    let alive = true;
    getOrganizationMembers(group.organization)
      .then((list) => { if (alive) setOrgPeople((list || []) as unknown as CandidatePerson[]); })
      .catch((error) => {
        console.error('Error loading organization beneficiaries:', error);
        if (alive) setOrgPeople([]);
      });
    return () => { alive = false; };
  }, [canManageMembers, addMode, group.organization]);

  const personalPhone = addMode === 'personal' ? personalSearchPhone(searchTerm) : '';

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    if (addMode === 'org') {
      const candidates = orgCandidates(orgPeople || [], members.map((m) => m.id));
      setSearchResults(
        searchOrgCandidates(candidates, searchTerm).map((p) => ({
          id: candidateUid(p),
          displayName: memberDisplayName(p, language),
          photoURL: (p as { photoURL?: string | null }).photoURL || null,
          maskedPhone: maskPhone(p.phone || p.phoneNumber),
        })),
      );
      return;
    }

    // Personal group: only a complete Saudi mobile number is looked up.
    if (!personalPhone) {
      setSearchResults([]);
      return;
    }
    let alive = true;
    const timeoutId = setTimeout(async () => {
      try {
        setSearching(true);
        const results = await searchUsersForGroup(group.id, personalPhone, language);
        if (alive) setSearchResults(results);
      } catch (error) {
        console.error('Error searching users:', error);
        if (alive) toast.error(t('support.errorSearchingUsers'));
      } finally {
        if (alive) setSearching(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timeoutId);
    };
  }, [searchTerm, personalPhone, addMode, orgPeople, members, group.id, language, t]);

  const handleAddMember = async (userId: string) => {
    if (!user) return;

    try {
      // 🛡️ Check if the user being added is an organization manager
      if (group.organization) {
        const isTargetOrgManager = await isOrganizationManager(group.organization, userId);
        if (isTargetOrgManager) {
          toast.info(t('support.orgManagerAutoMember'));
          setSearchTerm('');
          setSearchResults([]);
          return;
        }
      }

      // namat#30: one group per organization — a beneficiary already in
      // another group of this organization is offered as a move instead.
      const outcome = await addMemberOrOfferMove({
        groupId: group.id,
        groupName: group.name,
        userId,
        byUserId: user.uid,
        rtl: language === 'ar',
      });
      if (outcome.kind === 'declined') return;
      if (outcome.kind === 'blocked') {
        toast.error(outcome.message);
        return;
      }
      if (outcome.kind === 'added' || outcome.kind === 'moved') {
        toast.success(t('support.memberAdded'));
        setSearchTerm('');
        setSearchResults([]);
        // Refresh members list (virtual membership system)
        const { getGroupMembersVirtual } = await import('@/lib/firebase/queries/virtual-membership');
        setMembers(await getGroupMembersVirtual(group.id));
      } else {
        toast.error(t('support.errorAddingMember'));
      }
    } catch (error) {
      toast.error(t('support.errorAddingMember'));
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!user) return;

    try {
      const success = await removeGroupMember(group.id, memberId, user.uid);
      if (success) {
        toast.success(t('support.memberRemoved'));
        // Refresh members list using virtual membership system
        const { getGroupMembersVirtual } = await import('@/lib/firebase/queries/virtual-membership');
        const updatedMembers = await getGroupMembersVirtual(group.id);
        
        setMembers(updatedMembers);
        // ✅ Zero sync operations - virtual membership automatically reflects changes
        
      } else {
        toast.error(t('support.errorRemovingMember'));
      }
    } catch (error) {
      console.error('Error removing member:', error);
      // Show specific message for organization manager protection
      if (error instanceof Error && error.message.includes('Organization managers cannot be removed')) {
        toast.error(t('support.orgManagerCannotBeRemoved'));
      } else {
        toast.error(t('support.errorRemovingMember'));
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canManageMembers && (
        <div className="space-y-4 mb-4">
          <div className="relative">
            <Search className={`absolute top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
            <Input
              placeholder={addMode === 'org' ? t('support.searchByNameOrPhone') : t('support.searchByPhone')}
              inputMode={addMode === 'personal' ? 'tel' : undefined}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={language === 'ar' ? 'pr-10' : 'pl-10'}
              dir={language === 'ar' ? 'rtl' : 'ltr'}
            />
          </div>
          {searching && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!searching && searchTerm.trim() && searchResults.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  onClick={() => handleAddMember(user.id)}
                  className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
                  dir={language === 'ar' ? 'rtl' : 'ltr'}
                >
                  <div className="h-10 w-10 rounded-full bg-[#7dda55] flex items-center justify-center flex-shrink-0">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={user.displayName}
                        className="h-10 w-10 rounded-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <User className={`h-5 w-5 text-white ${user.photoURL ? 'hidden' : ''}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {user.displayName || user.maskedPhone}
                    </div>
                    {user.maskedPhone && (
                      <div className="text-xs text-muted-foreground truncate" dir="ltr">
                        {user.maskedPhone}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!searching && searchTerm.trim() && addMode === 'personal' && !personalPhone && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              {t('support.enterFullPhone')}
            </div>
          )}
          {!searching && searchTerm.trim() && (addMode === 'org' || personalPhone) && searchResults.length === 0 && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              {addMode === 'org' && orgPeople === null ? t('common.loading') : t('support.noUsersFound')}
            </div>
          )}
        </div>
      )}

      {group.organization && (
        <div className="text-xs text-muted-foreground p-3 bg-muted/50 rounded-lg border">
          {t('support.orgManagersIncludedNote')}
        </div>
      )}

      {members.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {t('support.noMembers')}
        </div>
      ) : (
        members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
            dir={language === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-[#7dda55] flex items-center justify-center">
                {member.photoURL ? (
                  <img
                    src={member.photoURL}
                    alt={memberDisplayName(member, language, member.email)}
                    className="h-10 w-10 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <User className={`h-5 w-5 text-white ${member.photoURL ? 'hidden' : ''}`} />
              </div>
              <div>
                <h4 className="font-medium">
                  {memberDisplayName(member, language, member.email)}
                </h4>
                <p className="text-sm text-muted-foreground">{member.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {member.role === 'editor' && (
                <Badge variant="secondary" className="text-xs">
                  {t('support.editor')}
                </Badge>
              )}
              {canManageMembers && member.id !== user?.uid && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveMember(member.id)}
                  className="h-8 w-8 text-destructive hover:text-destructive/90"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">{t('support.removeMember')}</span>
                </Button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
} 